import React, { useEffect, useMemo, useRef, useState, useCallback, useContext } from 'react';
import { useStyletron } from 'baseui';
import { Button } from 'baseui/button';
import { Notification, KIND } from 'baseui/notification';
import UserContext from 'commons-ui/src/context/user/userContext';
import { aiCompanionApi } from './api';
import VerdictPanel, { FlaggedGroup, GuardrailActionRow, VerdictTab } from './VerdictPanel';

interface ChatMessage {
	id?: string;
	role?: string;
	content?: string;
	messageType?: string;
	s3Url?: string;
	seq?: number;
	ts?: number;
	final?: boolean;
	[k: string]: unknown;
}

interface ViolationEntry {
	messageId: string;
	category: string;
	severity?: string | null;
	timestamp: number;
	shouldCount?: boolean;
	groupId: string;
}

interface ViolationGroup {
	groupId: string;
	category: string;
	severity?: string | null;
	timestamp: number;
	shouldCount?: boolean;
	messageIds: string[];
}

interface Props {
	userId: string;
	botId: string;
	onBack: () => void;
}

interface VoicePlayerProps {
	conversationUserId: string;
	s3Url?: string;
	tint: 'user' | 'assistant';
}

const PAGE_SIZE = 100;
// Auto-pagination safety cap. 50 pages × 100 messages = 5,000 messages.
const MAX_AUTO_PAGES = 50;

const formatTs = (ts?: number): string => {
	if (!ts) return '';
	return new Date(ts).toLocaleString();
};

const messageKey = (m: ChatMessage, idx: number): string => {
	if (m.id) return m.id;
	if (typeof m.seq === 'number') return `seq-${m.seq}`;
	return `idx-${idx}`;
};

const renderTextFor = (msg: ChatMessage): string => {
	if (msg.content && msg.content.length > 0) return msg.content;
	if (msg.messageType) return `[${msg.messageType}]`;
	return '[empty message]';
};

const isVoiceMessage = (msg: ChatMessage): boolean => {
	const type = (msg.messageType || '').toLowerCase();
	return type === 'voice' || type === 'audio';
};

const extractS3Key = (s3Url?: string): string | null => {
	if (!s3Url) return null;
	const m = /^s3:\/\/[^/]+\/(.+)$/.exec(s3Url);
	if (m) return m[1];
	// Path-style HTTPS: https://host/bucket/key
	try {
		const parsed = new URL(s3Url);
		const parts = parsed.pathname.split('/').filter(Boolean);
		const vhost = /^([\w.-]+)\.s3[.-]/.exec(parsed.hostname);
		if (vhost) return decodeURIComponent(parts.join('/'));
		if (parts.length >= 2) return decodeURIComponent(parts.slice(1).join('/'));
	} catch {
		/* not a URL */
	}
	return null;
};

const isAlreadySignedUrl = (s3Url?: string): boolean => {
	if (!s3Url) return false;
	if (!/^https?:\/\//i.test(s3Url)) return false;
	// Presigned URLs always include AWS query params; an unsigned HTTPS S3 URL
	// won't authenticate against the (private) bucket so we still need to sign.
	return /[?&](X-Amz-Signature|Signature)=/i.test(s3Url);
};

const presignVoiceMessages = async (page: ChatMessage[], userId: string): Promise<ChatMessage[]> => {
	const tasks = page.map(async (msg) => {
		if (!isVoiceMessage(msg)) return msg;
		const url = typeof msg.s3Url === 'string' ? msg.s3Url : undefined;
		if (!url || isAlreadySignedUrl(url)) return msg;
		const key = extractS3Key(url);
		if (!key) return msg;
		try {
			const result = await aiCompanionApi.getVoicePresignedUrl({ key, userId });
			if (result.success && result.data?.downloadUrl) {
				return { ...msg, s3Url: result.data.downloadUrl };
			}
		} catch {
			/* leave the original URL; VoicePlayer can retry on click */
		}
		return msg;
	});
	return Promise.all(tasks);
};

// Browser-side voice blob cache. Keyed by S3 key (stable across signing
// rounds), capped LRU so memory stays bounded across long review sessions.
// Each value is a blob: object URL — revoked on eviction so the audio bytes
// are reclaimed. Module-level so it survives ChatHistoryView remounts.
const VOICE_CACHE_MAX = 200;
const voiceBlobCache: Map<string, string> = new Map();

const cacheVoiceBlob = (s3Key: string, blobUrl: string): void => {
	if (voiceBlobCache.has(s3Key)) voiceBlobCache.delete(s3Key);
	voiceBlobCache.set(s3Key, blobUrl);
	while (voiceBlobCache.size > VOICE_CACHE_MAX) {
		const oldest = voiceBlobCache.keys().next().value as string | undefined;
		if (!oldest) break;
		const url = voiceBlobCache.get(oldest);
		voiceBlobCache.delete(oldest);
		if (url) URL.revokeObjectURL(url);
	}
};

const getCachedVoiceBlob = (s3Key: string): string | null => {
	const url = voiceBlobCache.get(s3Key);
	if (!url) return null;
	// Refresh LRU position on hit.
	voiceBlobCache.delete(s3Key);
	voiceBlobCache.set(s3Key, url);
	return url;
};

interface FetchToBlobResult {
	ok: boolean;
	blob?: Blob;
	corsBlocked?: boolean;
}

const fetchToBlob = async (url: string): Promise<FetchToBlobResult> => {
	try {
		const res = await fetch(url);
		if (res.ok) {
			return { ok: true, blob: await res.blob() };
		}
		return { ok: false };
	} catch {
		// fetch() rejects with TypeError on CORS / network failure. We
		// distinguish that case so the caller can fall back to letting the
		// <audio> element load the URL directly (no-cors playback).
		return { ok: false, corsBlocked: true };
	}
};

// Resolves to a playable URL for the given voice message. Order of operations:
//   1. Return cached blob: URL if we've already downloaded this clip.
//   2. Try fetching the (pre-signed) URL we already have; cache the bytes.
//   3. On 403/non-OK, ask the backend for a fresh signed URL and retry.
//   4. If the browser blocks the fetch (CORS not configured), fall back to
//      returning the signed URL itself so <audio src=...> can still play.
const resolvePlayableVoiceUrl = async (s3Key: string, candidateUrl: string | undefined, userId: string): Promise<string> => {
	const cached = getCachedVoiceBlob(s3Key);
	if (cached) return cached;

	if (candidateUrl && /^https?:\/\//i.test(candidateUrl)) {
		const first = await fetchToBlob(candidateUrl);
		if (first.ok && first.blob) {
			const blobUrl = URL.createObjectURL(first.blob);
			cacheVoiceBlob(s3Key, blobUrl);
			return blobUrl;
		}
		if (first.corsBlocked) return candidateUrl;
	}

	const refreshed = await aiCompanionApi.getVoicePresignedUrl({ key: s3Key, userId });
	if (!(refreshed.success && refreshed.data?.downloadUrl)) {
		throw new Error(refreshed.error || 'Failed to refresh signed URL');
	}
	const freshUrl = refreshed.data.downloadUrl;
	const second = await fetchToBlob(freshUrl);
	if (second.ok && second.blob) {
		const blobUrl = URL.createObjectURL(second.blob);
		cacheVoiceBlob(s3Key, blobUrl);
		return blobUrl;
	}
	if (second.corsBlocked) return freshUrl;
	throw new Error('Audio fetch failed');
};

const bubbleColorFor = (role: string): string => {
	if (role === 'user') return '#4f46e5';
	if (role === 'assistant') return '#ffffff';
	return '#fef3c7';
};

const alignFor = (role: string): 'flex-start' | 'flex-end' | 'center' => {
	if (role === 'user') return 'flex-end';
	if (role === 'assistant') return 'flex-start';
	return 'center';
};

const playButtonLabel = (loading: boolean, playing: boolean): string => {
	if (loading) return '…';
	if (playing) return '❚❚';
	return '▶';
};

const bubbleShadow = (isCurrent: boolean, isOtherViolation: boolean): string => {
	if (isCurrent) return '0 0 0 4px rgba(220, 38, 38, 0.35)';
	if (isOtherViolation) return '0 0 0 2px rgba(156, 163, 175, 0.25)';
	return 'none';
};

// Canonical action-type values live in cognilens enums.py as lowercase
// strings ("warned", "soft_blocked", etc.). Normalise here so banner colour
// classification doesn't silently fall through to the default gray when the
// upstream casing is anything other than upper-snake.
const BLOCK_TYPES = new Set(['blocked', 'soft_blocked', 'hard_blocked']);

const actionBannerColors = (actionType: string): { bg: string; border: string; fg: string; icon: string } => {
	const t = actionType.toLowerCase();
	if (BLOCK_TYPES.has(t)) return { bg: '#fee2e2', border: '#fca5a5', fg: '#991b1b', icon: '🚫' };
	if (t === 'flagged_for_review') return { bg: '#fef3c7', border: '#fcd34d', fg: '#92400e', icon: '🚩' };
	if (t === 'warned') return { bg: '#fff7ed', border: '#fdba74', fg: '#9a3412', icon: '⚠️' };
	return { bg: '#f3f4f6', border: '#d1d5db', fg: '#374151', icon: 'ℹ️' };
};

const VoicePlayer: React.FC<VoicePlayerProps> = ({ conversationUserId, s3Url, tint }) => {
	const [css] = useStyletron();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [loading, setLoading] = useState(false);
	const [playing, setPlaying] = useState(false);
	const [errMsg, setErrMsg] = useState<string | null>(null);

	const buttonBg = tint === 'user' ? '#ffffff' : '#4f46e5';
	const buttonFg = tint === 'user' ? '#4f46e5' : '#ffffff';

	const handleClick = async () => {
		const audio = audioRef.current;
		if (!audio) return;

		if (playing) {
			audio.pause();
			setPlaying(false);
			return;
		}

		if (audio.src && audio.src !== window.location.href) {
			try {
				await audio.play();
				setPlaying(true);
			} catch (e) {
				setErrMsg('Could not resume playback');
			}
			return;
		}

		const key = extractS3Key(s3Url);
		if (!key) {
			setErrMsg('Voice URL missing or in unrecognised format');
			return;
		}

		setLoading(true);
		setErrMsg(null);
		try {
			const playableUrl = await resolvePlayableVoiceUrl(key, s3Url, conversationUserId);
			audio.src = playableUrl;
			audio.onended = () => setPlaying(false);
			audio.onerror = () => {
				setPlaying(false);
				setErrMsg('Audio failed to load');
			};
			await audio.play();
			setPlaying(true);
		} catch (e) {
			setErrMsg(e instanceof Error ? e.message : 'Could not load audio');
		}
		setLoading(false);
	};

	return (
		<div className={css({ display: 'flex', alignItems: 'center', gap: '10px' })}>
			<button
				type='button'
				onClick={handleClick}
				disabled={loading}
				className={css({
					width: '32px',
					height: '32px',
					borderRadius: '50%',
					border: 'none',
					backgroundColor: buttonBg,
					color: buttonFg,
					cursor: loading ? 'wait' : 'pointer',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					fontSize: '14px',
					padding: 0
				})}
				aria-label={playing ? 'Pause voice message' : 'Play voice message'}>
				{playButtonLabel(loading, playing)}
			</button>
			<div className={css({ display: 'flex', flexDirection: 'column' })}>
				<span className={css({ fontSize: '13px', fontWeight: '500' })}>Voice message</span>
				{errMsg && <span className={css({ fontSize: '11px', color: '#fecaca' })}>{errMsg}</span>}
			</div>
			{/* eslint-disable-next-line jsx-a11y/media-has-caption */}
			<audio ref={audioRef} preload='none' />
		</div>
	);
};

interface QuickStatsProps {
	groups: ViolationGroup[];
	actions: GuardrailActionRow[];
	messageCount: number;
	flaggedMessageCount: number;
	css: ReturnType<typeof useStyletron>[0];
}

const QuickStats: React.FC<QuickStatsProps> = ({ groups, actions, messageCount, flaggedMessageCount, css }) => {
	const categoryCounts: Record<string, number> = {};
	const severityCounts: Record<string, number> = { LOW: 0, MED: 0, HIGH: 0, CRIT: 0, NULL: 0 };
	groups.forEach((g) => {
		categoryCounts[g.category] = (categoryCounts[g.category] || 0) + 1;
		const s = g.severity ?? 'NULL';
		severityCounts[s] = (severityCounts[s] || 0) + 1;
	});

	const actionCounts: Record<string, number> = {};
	actions.forEach((a) => {
		const t = String(a.actionType ?? 'OTHER');
		actionCounts[t] = (actionCounts[t] || 0) + 1;
	});

	const topCategoriesEntries = Object.entries(categoryCounts).sort(([, a], [, b]) => b - a);

	return (
		<div
			className={css({
				backgroundColor: '#fff',
				border: '1px solid #e5e7eb',
				borderRadius: '12px',
				padding: '12px 16px',
				marginBottom: '12px',
				fontSize: '12px',
				color: '#374151',
				display: 'flex',
				flexWrap: 'wrap',
				columnGap: '24px',
				rowGap: '6px'
			})}>
			<div>
				<span className={css({ color: '#6b7280' })}>Messages: </span>
				<strong>{messageCount}</strong>
			</div>
			<div>
				<span className={css({ color: '#6b7280' })}>Violations: </span>
				<strong>{groups.length}</strong>
				<span className={css({ color: '#9ca3af', marginLeft: '4px' })}>
					({flaggedMessageCount} flagged msg{flaggedMessageCount === 1 ? '' : 's'})
				</span>
			</div>
			{topCategoriesEntries.length > 0 && (
				<div>
					<span className={css({ color: '#6b7280' })}>Categories: </span>
					{topCategoriesEntries.slice(0, 4).map(([k, v], i) => (
						<span key={k}>
							{i > 0 ? ', ' : ''}
							<strong>{k}</strong> ({v})
						</span>
					))}
					{topCategoriesEntries.length > 4 && (
						<span className={css({ color: '#9ca3af' })}> +{topCategoriesEntries.length - 4}</span>
					)}
				</div>
			)}
			<div>
				<span className={css({ color: '#6b7280' })}>Severity: </span>
				{(['CRIT', 'HIGH', 'MED', 'LOW', 'NULL'] as const)
					.filter((s) => severityCounts[s])
					.map((s) => (
						<span key={s} className={css({ marginRight: '8px' })}>
							{s} <strong>{severityCounts[s]}</strong>
						</span>
					))}
			</div>
			{Object.keys(actionCounts).length > 0 && (
				<div>
					<span className={css({ color: '#6b7280' })}>Actions: </span>
					{Object.entries(actionCounts).map(([k, v], i) => (
						<span key={k}>
							{i > 0 ? ', ' : ''}
							<strong>{k}</strong> ({v})
						</span>
					))}
				</div>
			)}
		</div>
	);
};

const ChatHistoryView: React.FC<Props> = ({ userId, botId, onBack }) => {
	const [css] = useStyletron();
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [nextCursor, setNextCursor] = useState<number | undefined>(undefined);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [initialLoadDone, setInitialLoadDone] = useState<boolean>(false);
	const [autoPagesFetched, setAutoPagesFetched] = useState<number>(0);
	const [autoPaginating, setAutoPaginating] = useState<boolean>(true);
	const [violations, setViolations] = useState<ViolationEntry[]>([]);
	const [violationGroups, setViolationGroups] = useState<ViolationGroup[]>([]);
	const [actions, setActions] = useState<GuardrailActionRow[]>([]);
	// Violations and enforcement actions are reviewed in two independent
	// walks. The chat timeline mirrors whichever item is currently active in
	// the focused tab — a violation group's flagged message gets highlighted,
	// while an enforcement action's banner gets scrolled into view.
	const [activeTab, setActiveTab] = useState<VerdictTab>('violation');
	const [violationIndex, setViolationIndex] = useState<number>(0);
	const [enforcementIndex, setEnforcementIndex] = useState<number>(0);
	// Default to the most recent flagged message only; the full window expands
	// when the reviewer clicks the highlighted bubble.
	const [contextExpanded, setContextExpanded] = useState<boolean>(false);
	const [userActionLoading, setUserActionLoading] = useState<'block' | 'unblock' | null>(null);
	const [userActionNotification, setUserActionNotification] = useState<{
		message: string;
		kind: typeof KIND[keyof typeof KIND];
	} | null>(null);

	const scrollRef = useRef<HTMLDivElement | null>(null);
	const bottomRef = useRef<HTMLDivElement | null>(null);

	// Reviewer identity comes from the dashboard's existing Google-OAuth session
	// (UserContext is populated by commons-ui's UserProvider). If the user
	// isn't logged in, VerdictPanel surfaces a clear error before submit.
	const userCtx = useContext(UserContext);
	const reviewerEmail = userCtx?.user?.email || '';

	// For each chat message, the set of (category, groupId) entries that
	// flagged it. Used to draw badges + borders. The active group gets the
	// strong red highlight so the reviewer can see it in context (with the
	// surrounding messages around it); other-group violations stay visible
	// as a soft outline so the reviewer doesn't lose track of them.
	const violationsByMessageId = useMemo(() => {
		const map: Record<string, Array<{ category: string; groupId: string }>> = {};
		violations.forEach((v) => {
			if (!v.messageId) return;
			const list = map[v.messageId] || [];
			const already = list.some((x) => x.category === v.category && x.groupId === v.groupId);
			if (!already && v.category) {
				list.push({ category: v.category, groupId: v.groupId });
			}
			map[v.messageId] = list;
		});
		return map;
	}, [violations]);

	// Build one verdict row per detected event. The classifier saw a window of
	// messages as a single violation; the reviewer judges that whole window.
	// (Defined before markers so the marker list can reference it.)
	const flaggedGroups: FlaggedGroup[] = useMemo(() => {
		const messagesById: Record<string, ChatMessage> = {};
		messages.forEach((m) => {
			if (m.id) messagesById[m.id] = m;
		});
		return violationGroups.map((g) => ({
			groupId: g.groupId,
			category: g.category,
			severity: g.severity ?? null,
			timestamp: g.timestamp,
			messageIds: g.messageIds,
			messages: g.messageIds.map((mid) => {
				const m = messagesById[mid];
				return {
					messageId: mid,
					text: m ? renderTextFor(m) : undefined,
					role: m?.role
				};
			})
		}));
	}, [violationGroups, messages]);

	// Filter actions to this bot only — listGuardrailActions returns all actions
	// for the user across every bot.
	const botActions = useMemo(() => actions.filter((a) => a.botId === botId), [actions, botId]);

	// Each tab walks its own list independently. Clamp the index so a stale
	// value (e.g. left over from a previous (userId, botId)) never points past
	// the end of the array.
	const safeViolationIndex = Math.min(Math.max(violationIndex, 0), Math.max(flaggedGroups.length - 1, 0));
	const safeEnforcementIndex = Math.min(Math.max(enforcementIndex, 0), Math.max(botActions.length - 1, 0));
	const currentGroup: FlaggedGroup | undefined =
		activeTab === 'violation' && flaggedGroups.length > 0 ? flaggedGroups[safeViolationIndex] : undefined;
	const currentEnforcementActionId: string | undefined =
		activeTab === 'enforcement' && botActions.length > 0
			? String(botActions[safeEnforcementIndex]?._id ?? '') || undefined
			: undefined;

	// Full membership of the current violation group — used to suppress the
	// "other violation" dashed outline on messages that are part of the active
	// group but currently hidden by the collapsed view. Empty for enforcement
	// markers (which don't paint message bubbles).
	const currentGroupAllIdSet = useMemo(() => new Set(currentGroup?.messageIds ?? []), [currentGroup]);
	// What actually gets the red "current" highlight. Collapsed view shows only
	// the last messageId (the message that tripped the classifier); expanded
	// view shows the full window so the reviewer can read surrounding context.
	const currentHighlightedIdSet = useMemo(() => {
		const ids = currentGroup?.messageIds ?? [];
		if (ids.length === 0) return new Set<string>();
		if (contextExpanded) return new Set(ids);
		return new Set([ids[ids.length - 1]]);
	}, [currentGroup, contextExpanded]);

	// Combined timeline: messages + action banners interleaved by timestamp so the
	// reviewer sees enforcement events in the chronological flow.
	const timeline = useMemo(() => {
		const items: Array<
			{ kind: 'message'; ts: number; msg: ChatMessage } | { kind: 'action'; ts: number; act: GuardrailActionRow }
		> = [];
		messages.forEach((m) => {
			items.push({ kind: 'message', ts: typeof m.ts === 'number' ? m.ts : 0, msg: m });
		});
		botActions.forEach((a) => {
			const ts = typeof a.timestamp === 'number' ? a.timestamp : 0;
			items.push({ kind: 'action', ts, act: a });
		});
		// Stable sort by ts ascending. Messages with ts=0 stay where they are
		// (they're typically system stubs); actions always have a timestamp.
		items.sort((a, b) => a.ts - b.ts);
		return items;
	}, [messages, botActions]);

	const loadPage = useCallback(
		async (beforeSeq?: number) => {
			setLoading(true);
			setError(null);

			const scrollEl = scrollRef.current;
			const prevScrollHeight = scrollEl?.scrollHeight ?? 0;
			const prevScrollTop = scrollEl?.scrollTop ?? 0;

			try {
				const result = await aiCompanionApi.getChatHistory({
					userId,
					botId,
					beforeSeq,
					limit: PAGE_SIZE
				});
				if (result.success && result.data) {
					const rawPage = (result.data.messages || []) as ChatMessage[];
					// Pre-sign voice URLs at load time. Cognilens returns the raw
					// `s3://bucket/key` URL stored in MongoDB; the <audio> element
					// can't fetch those directly from a private bucket, so we mint
					// a short-lived signed GET here.
					const page = await presignVoiceMessages(rawPage, userId);
					if (beforeSeq === undefined) {
						setMessages(page);
					} else {
						setMessages((prev) => [...page, ...prev]);
					}
					setNextCursor(result.data.nextCursor);
					setHasMore(Boolean(result.data.hasMore));
				} else {
					setError(result.error || 'Failed to load chat history');
				}
			} catch (e) {
				setError('Network error. Please try again.');
			}
			setLoading(false);

			if (beforeSeq !== undefined && scrollEl) {
				requestAnimationFrame(() => {
					const newScrollHeight = scrollEl.scrollHeight;
					scrollEl.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
				});
			}
		},
		[userId, botId]
	);

	useEffect(() => {
		setMessages([]);
		setNextCursor(undefined);
		setHasMore(false);
		setInitialLoadDone(false);
		setAutoPagesFetched(0);
		setAutoPaginating(true);
		setViolations([]);
		setViolationGroups([]);
		setActions([]);
		setActiveTab('violation');
		setViolationIndex(0);
		setEnforcementIndex(0);
		setContextExpanded(false);
		loadPage(undefined).then(() => setInitialLoadDone(true));

		(async () => {
			try {
				const result = await aiCompanionApi.getViolatedMessages({ userId, botId });
				if (result.success && result.data) {
					const flatViolations = (result.data.violations || []).map((v) => ({
						messageId: v.messageId,
						category: v.category,
						severity: v.severity,
						timestamp: v.timestamp,
						shouldCount: v.shouldCount,
						groupId: v.groupId
					}));
					setViolations(flatViolations);

					const groupsFromBackend = (result.data.groups || []).map((g) => ({
						groupId: g.groupId,
						category: g.category,
						severity: g.severity,
						timestamp: g.timestamp,
						shouldCount: g.shouldCount,
						messageIds: g.messageIds || []
					}));
					if (groupsFromBackend.length > 0) {
						setViolationGroups(groupsFromBackend);
					} else {
						// Fallback: derive groups from the flat violations list when the
						// response has none. Older cognilens versions return only the
						// flat list, and we still want the wizard + active highlight to
						// work for those reviews.
						const byGroup: Record<
							string,
							{
								groupId: string;
								category: string;
								severity?: string | null;
								timestamp: number;
								shouldCount?: boolean;
								messageIds: string[];
							}
						> = {};
						flatViolations.forEach((v) => {
							const gid = v.groupId || `mid:${v.messageId}`;
							if (!byGroup[gid]) {
								byGroup[gid] = {
									groupId: gid,
									category: v.category,
									severity: v.severity,
									timestamp: v.timestamp,
									shouldCount: v.shouldCount,
									messageIds: []
								};
							}
							if (v.messageId && !byGroup[gid].messageIds.includes(v.messageId)) {
								byGroup[gid].messageIds.push(v.messageId);
							}
						});
						setViolationGroups(Object.values(byGroup).sort((a, b) => b.timestamp - a.timestamp));
					}
				}
			} catch (e) {
				/* non-fatal */
			}
		})();

		(async () => {
			try {
				const result = await aiCompanionApi.listGuardrailActions({
					queryType: 'BY_USER_ID',
					params: { zupeeUserId: userId },
					page: 1,
					pageSize: 200
				});
				if (result.success && result.data) {
					setActions((result.data.items || []) as GuardrailActionRow[]);
				}
			} catch (e) {
				/* non-fatal */
			}
		})();
	}, [userId, botId, loadPage]);

	useEffect(() => {
		if (initialLoadDone && bottomRef.current) {
			bottomRef.current.scrollIntoView({ behavior: 'auto' });
		}
	}, [initialLoadDone]);

	// When the reviewer advances to a new marker, scroll the chat to the
	// relevant point so the highlighted portion is centred on screen.
	// Violations tab → scroll to the active group's first messageId.
	// Enforcement tab → scroll to the active action banner.
	useEffect(() => {
		if (!initialLoadDone) return;
		setContextExpanded(false);
		let targetId: string | undefined;
		if (currentGroup) {
			const firstId = currentGroup.messageIds?.[0];
			if (firstId) targetId = `chat-msg-${firstId}`;
		} else if (currentEnforcementActionId) {
			targetId = `chat-act-${currentEnforcementActionId}`;
		}
		if (!targetId) return;
		const el = document.getElementById(targetId);
		if (el) {
			el.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	}, [activeTab, safeViolationIndex, safeEnforcementIndex, currentGroup, currentEnforcementActionId, initialLoadDone]);

	useEffect(() => {
		if (!initialLoadDone) return;
		if (loading) return;
		if (!autoPaginating) return;
		if (!hasMore) return;
		if (autoPagesFetched >= MAX_AUTO_PAGES) {
			setAutoPaginating(false);
			return;
		}
		setAutoPagesFetched((n) => n + 1);
		loadPage(nextCursor);
	}, [initialLoadDone, loading, autoPaginating, hasMore, autoPagesFetched, nextCursor, loadPage]);

	const handleBlockUser = async () => {
		if (!userId) return;
		setUserActionLoading('block');
		setUserActionNotification(null);
		try {
			const result = await aiCompanionApi.blockUser(userId);
			if (result?.success) {
				setUserActionNotification({ message: result.data?.message || 'User blocked', kind: KIND.positive });
			} else {
				setUserActionNotification({ message: result?.error || 'Failed to block user', kind: KIND.negative });
			}
		} catch (e) {
			setUserActionNotification({ message: 'Network error while blocking', kind: KIND.negative });
		}
		setUserActionLoading(null);
	};

	const handleUnblockUser = async () => {
		if (!userId) return;
		setUserActionLoading('unblock');
		setUserActionNotification(null);
		try {
			const result = await aiCompanionApi.unblockUser(userId);
			if (result?.success) {
				setUserActionNotification({ message: result.data?.message || 'User unblocked', kind: KIND.positive });
			} else {
				setUserActionNotification({ message: result?.error || 'Failed to unblock user', kind: KIND.negative });
			}
		} catch (e) {
			setUserActionNotification({ message: 'Network error while unblocking', kind: KIND.negative });
		}
		setUserActionLoading(null);
	};

	const renderActionBanner = (a: GuardrailActionRow, idx: number) => {
		const actionType = String(a.actionType ?? '');
		const c = actionBannerColors(actionType);
		const cat = String(a.category ?? '');
		const w = a.warningNumber ? `Warning #${a.warningNumber}` : null;
		const actionId = a._id ? String(a._id) : '';
		// Map this row back to its index in botActions (the same array
		// VerdictPanel walks). Clicking the banner jumps the verdict panel
		// to the enforcement tab and selects this action.
		const banderIdxInBotActions = actionId ? botActions.findIndex((x) => String(x._id ?? '') === actionId) : -1;
		const isActiveEnforcement = activeTab === 'enforcement' && banderIdxInBotActions === safeEnforcementIndex;
		const openEnforcement = () => {
			if (banderIdxInBotActions < 0) return;
			setContextExpanded(false);
			setActiveTab('enforcement');
			setEnforcementIndex(banderIdxInBotActions);
		};
		return (
			<div
				key={`act-${a._id ?? idx}-${a.timestamp ?? idx}`}
				id={a._id ? `chat-act-${actionId}` : undefined}
				role={banderIdxInBotActions >= 0 ? 'button' : undefined}
				tabIndex={banderIdxInBotActions >= 0 ? 0 : undefined}
				onClick={banderIdxInBotActions >= 0 ? openEnforcement : undefined}
				onKeyDown={
					banderIdxInBotActions >= 0
						? (e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									openEnforcement();
								}
						  }
						: undefined
				}
				title={banderIdxInBotActions >= 0 ? 'Click to review this enforcement action' : undefined}
				className={css({
					backgroundColor: c.bg,
					border: `1px solid ${c.border}`,
					color: c.fg,
					borderRadius: '8px',
					padding: '8px 14px',
					margin: '12px 0',
					fontSize: '12px',
					fontWeight: '600',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: '12px',
					cursor: banderIdxInBotActions >= 0 ? 'pointer' : 'default',
					boxShadow: isActiveEnforcement ? `0 0 0 2px ${c.border}` : 'none'
				})}>
				<div>
					<span className={css({ marginRight: '8px' })}>{c.icon}</span>
					{actionType.replace(/_/g, ' ')}
					{cat ? ` · ${cat}` : ''}
					{w ? ` · ${w}` : ''}
				</div>
				<div className={css({ fontSize: '11px', fontWeight: '500', opacity: 0.85 })}>{formatTs(a.timestamp as number)}</div>
			</div>
		);
	};

	const renderBubble = (msg: ChatMessage, idx: number) => {
		const role = (msg.role || '').toLowerCase();
		const isUser = role === 'user';
		const isAssistant = role === 'assistant';
		const text = renderTextFor(msg);
		const bubbleColor = bubbleColorFor(role);
		const textColor = isUser ? '#ffffff' : '#111827';
		const align = alignFor(role);
		const border = isAssistant ? '1px solid #e5e7eb' : 'none';
		const voice = isVoiceMessage(msg);
		const s3Url = typeof msg.s3Url === 'string' ? msg.s3Url : undefined;
		const violatedTags = msg.id ? violationsByMessageId[msg.id] : undefined;
		const isCurrent = Boolean(msg.id && currentHighlightedIdSet.has(msg.id));
		const isInCurrentGroup = Boolean(msg.id && currentGroupAllIdSet.has(msg.id));
		// Other-group violations stay visible as a soft dashed outline. Messages
		// that belong to the current group but are hidden by the collapsed view
		// render plain — the reviewer reveals them by clicking the highlighted
		// bubble.
		const isOtherViolation = !isInCurrentGroup && Boolean(violatedTags && violatedTags.length > 0);
		// Only the collapsed flagged bubble is the click target — clicking it
		// opens the context panel on the right and reveals the surrounding
		// window's other messageIds in the chat timeline.
		const canExpand = isCurrent && !contextExpanded;

		let bubbleBorder = border;
		if (isCurrent) bubbleBorder = '2px solid #dc2626';
		else if (isOtherViolation) bubbleBorder = '1px dashed #9ca3af';

		const navigateTo = (gid: string) => {
			const idxInGroups = flaggedGroups.findIndex((g) => g.groupId === gid);
			if (idxInGroups >= 0) {
				setActiveTab('violation');
				setViolationIndex(idxInGroups);
			}
		};

		const bubbleClass = css({
			padding: '10px 14px',
			backgroundColor: bubbleColor,
			color: textColor,
			border: bubbleBorder,
			borderRadius: '14px',
			fontSize: '14px',
			lineHeight: '1.45',
			whiteSpace: 'pre-wrap',
			wordBreak: 'break-word',
			boxShadow: bubbleShadow(isCurrent, isOtherViolation),
			cursor: canExpand ? 'pointer' : 'default'
		});
		const bubbleContent = voice ? (
			<VoicePlayer conversationUserId={userId} s3Url={s3Url} tint={isUser ? 'user' : 'assistant'} />
		) : (
			text
		);

		return (
			<div
				key={messageKey(msg, idx)}
				id={msg.id ? `chat-msg-${msg.id}` : undefined}
				className={css({ display: 'flex', justifyContent: align, marginBottom: '12px' })}>
				<div className={css({ maxWidth: '70%', display: 'flex', flexDirection: 'column', alignItems: align })}>
					{canExpand ? (
						<div
							role='button'
							tabIndex={0}
							title='Click to show surrounding context'
							onClick={() => setContextExpanded(true)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									setContextExpanded(true);
								}
							}}
							className={bubbleClass}>
							{bubbleContent}
						</div>
					) : (
						<div className={bubbleClass}>{bubbleContent}</div>
					)}
					{(isCurrent || isOtherViolation) && violatedTags && (
						<div
							className={css({
								display: 'flex',
								flexWrap: 'wrap',
								gap: '4px',
								marginTop: '4px',
								justifyContent: align
							})}>
							{violatedTags.map((tag) => {
								const tagIsCurrent = currentGroup?.groupId === tag.groupId;
								return (
									<button
										type='button'
										key={`${tag.groupId}-${tag.category}`}
										onClick={() => navigateTo(tag.groupId)}
										title={tagIsCurrent ? 'Active violation' : 'Click to review this violation'}
										className={css({
											fontSize: '10px',
											fontWeight: '600',
											color: tagIsCurrent ? '#fff' : '#374151',
											backgroundColor: tagIsCurrent ? '#dc2626' : '#e5e7eb',
											padding: '2px 8px',
											borderRadius: '10px',
											textTransform: 'uppercase',
											letterSpacing: '0.3px',
											border: 'none',
											cursor: 'pointer'
										})}>
										{tag.category}
									</button>
								);
							})}
						</div>
					)}
					<div className={css({ fontSize: '11px', color: '#9ca3af', marginTop: '4px', padding: '0 4px' })}>
						{role || 'unknown'} • {formatTs(msg.ts)}
						{typeof msg.seq === 'number' ? ` • seq ${msg.seq}` : ''}
					</div>
				</div>
			</div>
		);
	};

	return (
		<div className={css({ display: 'flex', flexDirection: 'column', height: '100%' })}>
			{/* Header bar */}
			<div
				className={css({
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '12px',
					gap: '12px'
				})}>
				<div>
					<Button size='compact' kind='secondary' onClick={onBack}>
						← Back to queue
					</Button>
				</div>
				<div className={css({ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' })}>
					<div className={css({ fontSize: '12px', color: '#6b7280' })}>
						<span>User </span>
						<strong className={css({ color: '#111' })}>{userId}</strong>
						<span className={css({ margin: '0 8px', color: '#d1d5db' })}>·</span>
						<span>Bot </span>
						<strong className={css({ color: '#111', fontFamily: 'monospace' })}>{botId}</strong>
					</div>
					<div className={css({ display: 'flex', gap: '8px' })}>
						<Button
							size='compact'
							kind='secondary'
							isLoading={userActionLoading === 'block'}
							disabled={userActionLoading !== null}
							onClick={handleBlockUser}>
							Block user
						</Button>
						<Button
							size='compact'
							kind='secondary'
							isLoading={userActionLoading === 'unblock'}
							disabled={userActionLoading !== null}
							onClick={handleUnblockUser}>
							Unblock user
						</Button>
					</div>
				</div>
			</div>

			{userActionNotification && (
				<div className={css({ marginBottom: '12px' })}>
					<Notification kind={userActionNotification.kind} onClose={() => setUserActionNotification(null)}>
						{userActionNotification.message}
					</Notification>
				</div>
			)}

			<QuickStats
				groups={violationGroups}
				actions={botActions}
				messageCount={messages.length}
				flaggedMessageCount={Object.keys(violationsByMessageId).length}
				css={css}
			/>

			{error && (
				<div className={css({ marginBottom: '12px' })}>
					<Notification kind={KIND.negative} onClose={() => setError(null)}>
						{error}
					</Notification>
				</div>
			)}

			{/* Two-column layout: timeline left, verdict right */}
			<div className={css({ display: 'flex', gap: '12px', flex: '1 1 auto', minHeight: 0 })}>
				<div
					ref={scrollRef}
					className={css({
						flex: '1 1 65%',
						overflowY: 'auto',
						backgroundColor: '#f8f9fa',
						border: '1px solid #e5e7eb',
						borderRadius: '12px',
						padding: '20px',
						minHeight: '400px'
					})}>
					{initialLoadDone && hasMore && autoPaginating && (
						<div className={css({ textAlign: 'center', fontSize: '12px', color: '#6b7280', marginBottom: '12px' })}>
							Loading older messages… {messages.length} loaded so far
						</div>
					)}
					{initialLoadDone && hasMore && !autoPaginating && (
						<div className={css({ display: 'flex', justifyContent: 'center', marginBottom: '12px' })}>
							<Button
								size='compact'
								kind='secondary'
								isLoading={loading}
								onClick={() => {
									setAutoPagesFetched(0);
									setAutoPaginating(true);
								}}>
								Load older messages ({messages.length} loaded; cap of {MAX_AUTO_PAGES * PAGE_SIZE} reached)
							</Button>
						</div>
					)}
					{initialLoadDone && !hasMore && messages.length > 0 && (
						<div className={css({ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginBottom: '12px' })}>
							— start of conversation ({messages.length} message{messages.length === 1 ? '' : 's'}) —
						</div>
					)}

					{!initialLoadDone && loading && (
						<div className={css({ textAlign: 'center', color: '#6b7280', padding: '20px' })}>Loading…</div>
					)}

					{initialLoadDone && messages.length === 0 && !error && (
						<div className={css({ textAlign: 'center', color: '#6b7280', padding: '40px' })}>
							No messages found for this user and bot.
						</div>
					)}

					{timeline.map((item, idx) => {
						if (item.kind === 'action') return renderActionBanner(item.act, idx);
						return renderBubble(item.msg, idx);
					})}
					<div ref={bottomRef} />
				</div>

				<div className={css({ flex: '0 0 38%', minWidth: '320px', display: 'flex', minHeight: 0 })}>
					{contextExpanded && currentGroup ? (
						<div
							className={css({
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
								width: '100%',
								backgroundColor: '#fff',
								border: '1px solid #e5e7eb',
								borderRadius: '12px',
								overflow: 'hidden'
							})}>
							<div
								className={css({
									padding: '12px 18px',
									borderBottom: '1px solid #e5e7eb',
									backgroundColor: '#f9fafb',
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
									gap: '8px'
								})}>
								<div>
									<h4 className={css({ fontSize: '15px', fontWeight: '600', color: '#111', margin: 0 })}>
										Context
									</h4>
									<div className={css({ fontSize: '11px', color: '#6b7280', marginTop: '2px' })}>
										<span className={css({ color: '#dc2626', fontWeight: '600' })}>{currentGroup.category}</span>
										<span className={css({ margin: '0 6px', color: '#d1d5db' })}>·</span>
										sys: {currentGroup.severity ?? 'null'}
										<span className={css({ margin: '0 6px', color: '#d1d5db' })}>·</span>
										{currentGroup.messages.length} message
										{currentGroup.messages.length === 1 ? '' : 's'}
									</div>
								</div>
								<Button size='compact' kind='secondary' onClick={() => setContextExpanded(false)}>
									← Back
								</Button>
							</div>
							<div className={css({ flex: '1 1 auto', overflowY: 'auto', padding: '12px 14px' })}>
								{currentGroup.messages.map((m, mIdx) => {
									const isTrigger = mIdx === currentGroup.messages.length - 1;
									return (
										<div
											key={m.messageId}
											className={css({
												marginBottom: '10px',
												padding: '8px 10px',
												borderRadius: '8px',
												backgroundColor: isTrigger ? '#fef2f2' : '#f9fafb',
												border: isTrigger ? '1px solid #fca5a5' : '1px solid #e5e7eb'
											})}>
											<div
												className={css({
													fontSize: '11px',
													color: '#6b7280',
													marginBottom: '4px',
													display: 'flex',
													justifyContent: 'space-between',
													gap: '8px'
												})}>
												<span className={css({ fontWeight: '600', color: '#374151' })}>
													{m.role || 'unknown'}
													{isTrigger && (
														<span className={css({ color: '#dc2626', marginLeft: '6px' })}>
															· trigger
														</span>
													)}
												</span>
												<span className={css({ fontFamily: 'monospace' })}>{m.messageId}</span>
											</div>
											<div
												className={css({
													fontSize: '13px',
													color: '#111',
													whiteSpace: 'pre-wrap',
													wordBreak: 'break-word'
												})}>
												{m.text || '[empty message]'}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					) : (
						<VerdictPanel
							userId={userId}
							botId={botId}
							reviewerEmail={reviewerEmail}
							flagged={flaggedGroups}
							actions={botActions}
							activeTab={activeTab}
							onActiveTabChange={setActiveTab}
							violationIndex={safeViolationIndex}
							onViolationIndexChange={setViolationIndex}
							enforcementIndex={safeEnforcementIndex}
							onEnforcementIndexChange={setEnforcementIndex}
							onSubmitted={onBack}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default ChatHistoryView;
